import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@shared-kernel/primary/shared/shadcn/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared-kernel/primary/shared/shadcn/ui/alert-dialog";
import { Checkbox } from "@shared-kernel/primary/shared/shadcn/ui/checkbox";
import { FieldLimitCounter } from "@shared-kernel/primary/shared/field-limit-counter/field-limit-counter";
import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { ORDERED_MAIN_SPECIALIZATIONS, SPECIALIZATION_TREE, getMainOf } from "@academy-context/shared/domain/types/enums/specializations";
import type { LeafId, MAIN_SPECIALIZATIONS } from "@academy-context/shared/domain/types/enums/specializations";
import { SPECIALIZATIONS_MAX } from "./specializations-picker.schema";

interface Props {
  name: string;
}

export const SpecializationsPicker = ({ name }: Props) => {
  const { control, setValue } = useFormContext();
  const rawWatched = useWatch({ control, name });
  const watched = useMemo(() => (rawWatched ?? []) as LeafId[], [rawWatched]);

  const selectedByMain = useMemo(() => {
    const map = new Map<MAIN_SPECIALIZATIONS, Set<LeafId>>();
    watched.forEach(leafId => {
      const main = getMainOf(leafId);
      if (!map.has(main)) map.set(main, new Set());
      map.get(main)!.add(leafId);
    });
    return map;
  }, [watched]);

  const selectedGroupIds = useMemo(() => {
    const set = new Set<string>();
    watched.forEach(leafId => {
      const parts = leafId.split(".");
      if (parts.length >= 3) set.add(`${parts[0]}.${parts[1]}`);
    });
    return set;
  }, [watched]);

  const [userOpened, setUserOpened] = useState<Set<MAIN_SPECIALIZATIONS>>(() => new Set(selectedByMain.keys()));
  const [openedGroups, setOpenedGroups] = useState<Set<string>>(() => new Set(selectedGroupIds));
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);

  useEffect(() => {
    setUserOpened(prev => {
      let changed = false;
      const next = new Set(prev);
      selectedByMain.forEach((_, main) => {
        if (!next.has(main)) {
          next.add(main);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedByMain]);

  useEffect(() => {
    setOpenedGroups(prev => {
      let changed = false;
      const next = new Set(prev);
      selectedGroupIds.forEach(groupId => {
        if (!next.has(groupId)) {
          next.add(groupId);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedGroupIds]);

  const isAtLimit = watched.length >= SPECIALIZATIONS_MAX;

  const setSpecializations = (next: LeafId[]) => setValue(name, next, { shouldDirty: true, shouldValidate: true });

  const toggleMain = (main: MAIN_SPECIALIZATIONS) => {
    if (selectedByMain.has(main)) {
      setSpecializations(watched.filter(id => getMainOf(id) !== main));
    }
    setUserOpened(prev => {
      const next = new Set(prev);
      if (next.has(main)) next.delete(main);
      else next.add(main);
      return next;
    });
  };

  const toggleLeaf = (leafId: LeafId) => {
    if (watched.includes(leafId)) {
      setSpecializations(watched.filter(id => id !== leafId));
      return;
    }
    if (isAtLimit) {
      setLimitDialogOpen(true);
      return;
    }
    const next = [...watched, leafId];
    setSpecializations(next);
    if (next.length >= SPECIALIZATIONS_MAX) setLimitDialogOpen(true);
  };

  return (
    <FormField
      name={name}
      render={() => (
        <FormItem>
          <div className="space-x-2">
            <FormLabel>Spécialités</FormLabel>
            <FieldLimitCounter
              current={watched.length}
              threshold={SPECIALIZATIONS_MAX}
              mode="max"
              suffix="spécialités sélectionnées"
              data-testid="specializations-counter"
            />
          </div>
          <FormDescription>
            Vos domaines d'expertise — dans la limite de {SPECIALIZATIONS_MAX}. Veuillez d'abord sélectionner la ou les grandes catégories
            qui vous concernent, parmi les {ORDERED_MAIN_SPECIALIZATIONS.length} proposées. Puis sélectionnez vos spécialités dans la
            perspective de leur utilisation par les élèves pour filtrer leurs recherches.
          </FormDescription>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {ORDERED_MAIN_SPECIALIZATIONS.map(main => {
              const node = SPECIALIZATION_TREE[main];
              const isOpen = userOpened.has(main);
              return (
                <button
                  key={main}
                  type="button"
                  onClick={() => toggleMain(main)}
                  aria-pressed={isOpen}
                  className={cn(
                    "flex flex-col items-center justify-center gap-2 rounded-md border p-4 text-center transition-colors",
                    isOpen ? "border-primary bg-primary/5 ring-2 ring-primary" : "hover:bg-muted/50",
                  )}
                  data-testid={`specialization-category-card-${main}`}
                >
                  <span className="text-2xl" aria-hidden="true">
                    {node.emoji}
                  </span>
                  <span className="text-sm font-medium">{node.label}</span>
                </button>
              );
            })}
          </div>

          {userOpened.size === 0 ? (
            <p className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground" data-testid="specializations-empty-hint">
              Sélectionnez au moins un domaine ci-dessus pour afficher les spécialités disponibles
            </p>
          ) : (
            <div className="space-y-3">
              {ORDERED_MAIN_SPECIALIZATIONS.filter(main => userOpened.has(main)).map(main => {
                const node = SPECIALIZATION_TREE[main];
                const selected = selectedByMain.get(main) ?? new Set<LeafId>();
                return (
                  <div key={main} className="rounded-md border p-4" data-testid={`specialization-category-panel-${main}`}>
                    <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                      <span aria-hidden="true">{node.emoji}</span>
                      <span>{node.label}</span>
                    </div>

                    {node.kind === "flat" ? (
                      <div className="space-y-2">
                        {node.children.map(leaf => {
                          const checked = selected.has(leaf.id);
                          const dimmed = !checked && isAtLimit;
                          return (
                            <label
                              key={leaf.id}
                              className={cn("flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm", dimmed && "opacity-50")}
                              data-testid={`specialization-leaf-row-${leaf.id}`}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => toggleLeaf(leaf.id)}
                                data-testid={`specialization-leaf-checkbox-${leaf.id}`}
                              />
                              <span>{leaf.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <Accordion
                        type="multiple"
                        className="w-full"
                        value={Array.from(openedGroups)}
                        onValueChange={vals => setOpenedGroups(new Set(vals))}
                      >
                        {node.children.map(group => (
                          <AccordionItem key={group.id} value={group.id} data-testid={`specialization-group-${group.id}`}>
                            <AccordionTrigger className="text-sm">{group.label}</AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-2">
                                {group.children.map(leaf => {
                                  const checked = selected.has(leaf.id);
                                  const disabled = !checked && isAtLimit;
                                  return (
                                    <label
                                      key={leaf.id}
                                      className={cn(
                                        "flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm",
                                        disabled && "cursor-not-allowed opacity-50",
                                      )}
                                      data-testid={`specialization-leaf-row-${leaf.id}`}
                                    >
                                      <Checkbox
                                        checked={checked}
                                        disabled={disabled}
                                        onCheckedChange={() => toggleLeaf(leaf.id)}
                                        data-testid={`specialization-leaf-checkbox-${leaf.id}`}
                                      />
                                      <span>{leaf.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <FormMessage />

          <AlertDialog open={limitDialogOpen} onOpenChange={setLimitDialogOpen}>
            <AlertDialogContent className="max-w-md" data-testid="specializations-limit-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Limite atteinte : {SPECIALIZATIONS_MAX} spécialités maximum</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>Vous avez sélectionné {SPECIALIZATIONS_MAX} spécialités. Pour en ajouter d'autres, décochez-en d'abord certaines.</p>
                    <p>
                      Ces spécialités servent de filtres de recherche : inutile de tout cocher. Sélectionnez plutôt celles qui vous
                      distinguent, ce sont elles qui vous mettront en avant dans les résultats.
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction>OK</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </FormItem>
      )}
    />
  );
};
